import React, { useState } from "react";
import { useHistory, Redirect } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  Container,
  Paper,
  Grid,
  Typography,
  makeStyles,
  IconButton,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import { CreatePool } from "../../CoinSwapper/CoinField";
import CoinDialog from "../../CoinSwapper/CoinDialog";
import LoadingButton from "../../Components/LoadingButton";

import { addPool } from "../StackingFunctions";
import { refreshPoolStake } from "../../apiFunctions";
import { getBalanceAndSymbol } from "../../ethereumFunctions";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(3),
    maxWidth: 700,
    margin: "auto",
  },
  title: {
    textAlign: "center",
    padding: theme.spacing(0.5),
  },
  buttonIconBox: {
    background: theme.palette.background.default,
    width: "32px",
    height: "32px",
    borderRadius: "12px",
    cursor: "pointer",
  },
  buttonIconSVG: {
    fontSize: "14px",
  },
  hr: {
    width: "100%",
    color: theme.palette.grey[400],
  },
}));

function CreateStakingPool({ network }) {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);

  const [dialog1Open, setDialog1Open] = useState(false);
  const [dialog2Open, setDialog2Open] = useState(false);

  // Stores the current value of their respective text box
  const [fieldValue, setFieldValue] = useState("");

  // Stores data about their respective coin
  const [coin1, setCoin1] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });
  const [coin2, setCoin2] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });

  // These functions take an HTML event, pull the data out and puts it into a state variable.
  const handleChange = {
    field: (e) => {
      setFieldValue(e.target.value);
    },
  };

  // Switches the top and bottom coins, this is called when users hit the swap button or select the opposite
  // token in the dialog (e.g. if coin1 is TokenA and the user selects TokenB when choosing coin2)
  const switchFields = () => {
    setCoin1(coin2);
    setCoin2(coin1);
  };

  // Called when the dialog window for coin1 exits
  const onToken1Selected = (address, symbol) => {
    // Close the dialog window
    setDialog1Open(false);

    // If the user inputs the same token, we want to switch the data in the fields
    if (address === coin2.address) {
      switchFields();
    }
    // We only update the values if the user provides a token
    else if (address && symbol) {
      // Getting some token data is async, so we need to wait for the data to return, hence the promise
      getBalanceAndSymbol(
        network.account,
        address,
        network.wethAddress,
        network.coins
      ).then((data) => {
        setCoin1({
          address: address,
          symbol: symbol,
          balance: data.balance,
          decimal: data.decimal,
        });
      });
    }
  };

  // Called when the dialog window for coin2 exits
  const onToken2Selected = (address, symbol) => {
    // Close the dialog window
    setDialog2Open(false);

    // If the user inputs the same token, we want to switch the data in the fields
    if (address === coin1.address) {
      switchFields();
    }
    // We only update the values if the user provides a token
    else if (address && symbol) {
      // Getting some token data is async, so we need to wait for the data to return, hence the promise
      getBalanceAndSymbol(
        network.account,
        address,
        network.wethAddress,
        network.coins
      ).then((data) => {
        setCoin2({
          address: address,
          symbol: symbol,
          balance: data.balance,
          decimal: data.decimal,
        });
      });
    }
  };

  // Determines whether the button should be enabled or not
  const isButtonEnabled = () => {
    // If both coins have been selected, and a valid float has been entered for both, which are less than the user's balances, then return true
    const parsedInput = parseFloat(fieldValue);
    return (
      coin1.address && coin2.address && !isNaN(parsedInput) && 0 < parsedInput
    );
  };

  const create = () => {
    console.log("Attempting to creating pool...");

    setLoading(true);

    addPool(
      network.stacking,
      network.signer,
      coin1.address,
      coin2.address,
      fieldValue
    )
      .then(async () => {
        // If the transaction was successful, we clear to input to make sure the user doesn't accidental redo the transfer
        enqueueSnackbar("Pool Create Successful", {
          variant: "success",
          autoHideDuration: 5000,
        });

        await refreshPoolStake(network.chainID);

        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        enqueueSnackbar("Pool Create Failed (" + e.reason + ")", {
          variant: "error",
          autoHideDuration: 5000,
        });
      });
  };

  return (
    <>
      {network.cheinID != 56 ? (
        <div>
          <CoinDialog
            open={dialog1Open}
            onClose={onToken1Selected}
            coins={network.coins}
            account={network.account}
            weth={network.wethAddress}
            chainId={network.chainID}
          />{" "}
          <CoinDialog
            open={dialog2Open}
            onClose={onToken2Selected}
            coins={network.coins}
            signer={network.signer}
            account={network.account}
            provider={network.provider}
            weth={network.wethAddress}
            chainId={network.chainID}
          />{" "}
          <Container>
            <Paper elevation={0} className={classes.paperContainer}>
              <div className="d-flex align-items-center justify-content-between">
                <IconButton
                  onClick={() => history.push("/update-staking-pool")}
                  variant="contained"
                  className={classes.buttonIconBox}
                >
                  <ArrowBackIcon className={classes.buttonIconSVG} />
                </IconButton>
                <Typography variant="h6" className={classes.title}>
                  Create Pool
                </Typography>
                <span style={{ width: "32px" }}></span>
              </div>
              <Grid
                container
                direction="column"
                alignItems="center"
                spacing={2}
              >
                <hr className={classes.hr} />
                <Grid item xs={12} className="w-100">
                  <CreatePool
                    activeField1={true}
                    activeFiel2={true}
                    value={fieldValue}
                    onChange={handleChange.field}
                    onClick1={() => setDialog1Open(true)}
                    onClick2={() => setDialog2Open(true)}
                    symbol1={coin1.symbol}
                    symbol2={coin2.symbol}
                  />{" "}
                </Grid>{" "}
                <hr className={classes.hr} />
              </Grid>
              <Grid
                container
                direction="column"
                alignItems="center"
                spacing={2}
              >
                <LoadingButton
                  loading={loading}
                  valid={isButtonEnabled()}
                  success={false}
                  fail={false}
                  onClick={create}
                >
                  Create
                </LoadingButton>{" "}
              </Grid>{" "}
            </Paper>
          </Container>
        </div>
      ) : (
        <Redirect to="/invest" />
      )}
    </>
  );
}

export default CreateStakingPool;
